import { useState, useEffect, useCallback } from "react";
import api, { setAuthToken, setUnauthorizedHandler } from "../services/api";

// NEW: checks if a JWT token is expired
const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split(".")[1])); // decode middle part of JWT
    if (!payload.exp) return false; // no expiry field, assume it's fine (or treat as expired — see note below)
    return payload.exp * 1000 < Date.now(); // exp is in seconds, Date.now() is ms
  } catch (err) {
    return true; // can't decode = treat as invalid
  }
};

/**
 * Reshape a raw user document into the single client-side user shape.
 *
 * /api/users/me returns the stored document; /api/auth/login returns a
 * flattened view of the same user. They disagree on two fields, and spreading
 * one over the other silently corrupts both:
 *
 *   termsAccepted    document: { accepted, acceptedAt, … }   login: boolean
 *   temporaryAccess  document: { enabled, expiresAt }        login: null unless enabled
 *
 * The login shape is what every consumer expects — `termsAccepted === true`
 * gates the terms modal, and TrialContext reads `temporaryAccess?.expiresAt`
 * and must not see a date on a disabled account. Fields are therefore mapped
 * explicitly rather than spread: a field the document gains later cannot
 * change the shape of one already in use.
 */
function toClientUser(doc, fallback = {}) {
  return {
    ...fallback,
    id: doc._id || doc.id || fallback.id,
    username: doc.username ?? fallback.username,
    email: doc.email ?? fallback.email,
    fullName: doc.fullName ?? null,
    phoneNumber: doc.phoneNumber ?? fallback.phoneNumber,
    role: doc.role ?? fallback.role,
    unrestrictedTargets: !!doc.unrestrictedTargets,
    termsAccepted:
      doc.termsAccepted && typeof doc.termsAccepted === "object"
        ? !!doc.termsAccepted.accepted
        : !!doc.termsAccepted,
    temporaryAccess: doc.temporaryAccess?.enabled
      ? { expiresAt: doc.temporaryAccess.expiresAt ?? null }
      : null,
  };
}

const useAuth = () => {
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    const storedUser = localStorage.getItem("user");

    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
      setAuthToken(storedToken);

      // Render immediately from the stored copy, then reconcile with the
      // server. Without this, admin-set flags (free URL entry, role changes)
      // only reach a user after they log out and back in, because the stored
      // copy is whatever login returned and is never refreshed.
      //
      // Best-effort: a failure leaves the stored user in place. A genuinely
      // invalid session is handled by the 401 interceptor below, not here.
      api
        .getProfile()
        .then((res) => {
          const fresh = res?.data?.user;
          if (!fresh) return;
          const merged = toClientUser(fresh, JSON.parse(storedUser));
          localStorage.setItem("user", JSON.stringify(merged));
          setUser(merged);
        })
        .catch(() => {});
    }
    setIsLoading(false);

    // Wire up auto-logout: any 401/403 from a protected endpoint clears the session.
    setUnauthorizedHandler(() => {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      setToken(null);
      setUser(null);
      setAuthToken(null);
    });

    return () => setUnauthorizedHandler(null);
  }, []);

  const login = useCallback(async (username, password) => {
    const response = await api.login(username.trim(), password);
    const data = response.data;
    localStorage.setItem("token", data.token);
    localStorage.setItem("user", JSON.stringify(data.user));
    setToken(data.token);
    setUser(data.user);
    setAuthToken(data.token);
    return data;
  }, []);

  const register = useCallback(
    async (username, email, password, phoneNumber) => {
      const response = await api.register(
        username.trim(),
        email.trim(),
        password,
        phoneNumber?.trim(),
      );
      const data = response.data;
      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      setToken(data.token);
      setUser(data.user);
      setAuthToken(data.token);
      return data;
    },
    [],
  );

  /**
   * Adopt a session that was established without a password.
   *
   * The guest OTP flow returns the same { token, user } payload as /auth/login,
   * but it comes from a different endpoint, so there is nothing for `login` to
   * call. Storing it identically is what keeps the rest of the app from having
   * to know how a session was obtained.
   */
  const adoptSession = useCallback((data) => {
    if (!data?.token || !data?.user) return;
    localStorage.setItem("token", data.token);
    localStorage.setItem("user", JSON.stringify(data.user));
    setToken(data.token);
    setUser(data.user);
    setAuthToken(data.token);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } catch (err) {
      console.warn("Logout failed:", err);
    }
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setToken(null);
    setUser(null);
    setAuthToken(null);
  }, []);

  const acceptTerms = useCallback(async () => {
    await api.acceptTerms("2025-05-27");
    setUser((prev) => {
      console.log("Updating user to accept terms:", prev);
      if (!prev) return prev;
      const updated = { ...prev, termsAccepted: true };
      localStorage.setItem("user", JSON.stringify(updated));
      return updated;
    });
  }, []);

  const getToken = useCallback(() => token, [token]);

  const updateUser = useCallback((updates) => {
    setUser((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, ...updates };
      localStorage.setItem("user", JSON.stringify(updated));
      return updated;
    });
  }, []);

  const termsAccepted = user?.termsAccepted === true;
  // Link-invited account: no project, a fixed run allowance, and no access to
  // project or admin screens. See backend/config/guestAccess.js.
  const isGuest = user?.role === "guest";

  return {
    token,
    user,
    isLoading,
    login,
    register,
    adoptSession,
    logout,
    acceptTerms,
    updateUser,
    getToken,
    isAuthenticated: !!token,
    termsAccepted,
    isGuest,
  };
};

export default useAuth;
